import {useCallback, useEffect, useMemo, useState} from 'react';

import appStore from 'client/AppStore';
import {getTeacherHasClassSubjects} from './GlobalNav.queries';
import NAV_ITEMS from './NAV_ITEMS';
import type {DropdownNavItem, MainNavItem, NavItemZone} from './types';
import sessionStore from 'client/Session/SessionStore';

export default function useNavItems() {
  const hasValidSession = sessionStore.hasValidSession();
  const isSignUpPage = window.location.pathname.includes('/signup');
  const linkZone = appStore.isZoneDennis() ? 'dennis' : 'albert';

  const [isTeacherWithClassSubjects, setIsTeacherWithClassSubjects] = useState(false);

  useEffect(() => {
    if (sessionStore.isTeacher()) {
      (async () => {
        const teacherHasClassSubjects = await getTeacherHasClassSubjects();
        setIsTeacherWithClassSubjects(teacherHasClassSubjects);
      })();
    }
  }, [setIsTeacherWithClassSubjects]);

  const filterByPermissionPredicates = useCallback(
    ({permissionPredicates}: DropdownNavItem | MainNavItem) =>
      permissionPredicates === undefined
        ? true
        : permissionPredicates.some((predicate) => predicate({isTeacherWithClassSubjects})),
    [isTeacherWithClassSubjects]
  );

  const filteredNavItems: NavItemZone = useMemo(() => {
    if (!hasValidSession && isSignUpPage) {
      return {
        dropdownNavItems: [],
        mainNavItems: []
      };
    }

    const dropdownNavItems = NAV_ITEMS[linkZone].dropdownNavItems.filter(
      filterByPermissionPredicates
    );
    const mainNavItems = NAV_ITEMS[linkZone].mainNavItems.filter(filterByPermissionPredicates);

    return {
      dropdownNavItems,
      mainNavItems
    };
  }, [filterByPermissionPredicates, hasValidSession, isSignUpPage, linkZone]);

  function getIsMatchingRoute(navItem: MainNavItem) {
    const {pathname} = window.location;
    const {matchingRoutes = [], route: linkRoute, routesArr = []} = navItem;

    let ret = false;

    if (routesArr.length > 0) {
      ret = routesArr.some((route) => pathname.startsWith(route));
    } else if (
      /**
       * This ensures that the Subjects header link will contain the active class
       * regardless of which group/subject combination is rendered. It will be replaced soon.
       */
      matchingRoutes.includes(pathname) ||
      linkRoute === pathname ||
      pathname.startsWith(linkRoute) ||
      matchingRoutes.filter((path) => pathname.indexOf(path) === 0).length > 0
    ) {
      ret = true;
    }
    return ret;
  }

  return {
    dropdownNavItems: filteredNavItems.dropdownNavItems,
    getIsMatchingRoute,
    mainNavItems: filteredNavItems.mainNavItems
  };
}
